import Title from '@cohort/components-xps/components/Title';
import type {RadioGroupTogglerOption} from '@cohort/wallet/components/RadioGroupToggler';
import RadioGroupToggler from '@cohort/wallet/components/RadioGroupToggler';
import {AboutBrand} from '@cohort/wallet/components/space/AboutBrand';
import JourneysList from '@cohort/wallet/components/space/JourneysList';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {partition} from 'remeda';

type JourneysListFilters = 'active' | 'completed';

const JourneyListPage = (): JSX.Element => {
  const {space} = useSpaceContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.journeyListPage'});
  const [filter, setFilter] = useState<JourneysListFilters>('active');

  if (space === undefined) {
    return <Fragment />;
  }

  const options: RadioGroupTogglerOption[] = [
    {
      value: 'active',
      label: t('filterActive'),
    },
    {
      value: 'completed',
      label: t('filterCompleted'),
    },
  ];

  const journeysPartition = partition(
    space.journeys,
    campaign =>
      (campaign.journey.participation?.completedAt ?? null) !== null ||
      campaign.availableSupply === 0 ||
      campaign.status === 'ended'
  );

  return (
    <SpacePage
      title={t('title')}
      leftPanel={<AboutBrand />}
      tracking={{
        namespace: 'journeys.list',
      }}
    >
      <div className="mb-4 flex flex-col items-center justify-between gap-4 md:flex-row">
        <Title content={t('title')} classes="text-2xl" />
        <div className="w-full lg:w-[400px]">
          <RadioGroupToggler
            options={options}
            onChange={option => setFilter(option.value as JourneysListFilters)}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            currentOption={options.find(o => o.value === filter)!}
            tracking={{
              namespace: 'journeys.list.filter',
              metadata: {
                journeyStatus: filter,
              },
            }}
          />
        </div>
      </div>
      <JourneysList
        campaigns={filter === 'completed' ? journeysPartition[0] : journeysPartition[1]}
      />
    </SpacePage>
  );
};

export default JourneyListPage;
